import { useEffect, useState } from 'react';
import { api, apiBaseUrl } from '../lib/api';
import { useAuthStore } from '../store/auth';

interface Props {
  callId: string;
  hasRecording: boolean;
}

export function CallRecordingPlayer({ callId, hasRecording }: Props): JSX.Element {
  const user = useAuthStore((s) => s.user);
  const [src, setSrc] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [checked, setChecked] = useState(false);
  const [diagnosing, setDiagnosing] = useState(false);
  const [diagnostic, setDiagnostic] = useState<string | null>(null);
  const canDiagnose = user?.role === 'admin' || user?.role === 'supervisor';

  useEffect(() => {
    let cancelled = false;
    setSrc(null);
    setError(null);
    setChecked(false);
    void api
      .get<{ url: string; expiresAt: string }>(`/calls/${callId}/recording-url`)
      .then((res) => {
        if (!cancelled) {
          setSrc(`${apiBaseUrl}${res.url}`);
          setChecked(true);
        }
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        const code = (err as { code?: string }).code;
        setError(code === 'NO_RECORDING' ? null : 'Recording unavailable');
        setChecked(true);
      });
    return () => {
      cancelled = true;
    };
  }, [callId, hasRecording]);

  if (!hasRecording && checked && !src) {
    return (
      <div className="space-y-2">
        <div className="text-xs text-ink-500 italic">No recording available</div>
        {canDiagnose && (
          <RecordingDiagnosticButton
            callId={callId}
            diagnosing={diagnosing}
            diagnostic={diagnostic}
            onDiagnosing={setDiagnosing}
            onDiagnostic={setDiagnostic}
          />
        )}
      </div>
    );
  }

  if (error) {
    return (
      <div className="space-y-2">
        <div className="text-xs text-red-600">{error}</div>
        {canDiagnose && (
          <RecordingDiagnosticButton
            callId={callId}
            diagnosing={diagnosing}
            diagnostic={diagnostic}
            onDiagnosing={setDiagnosing}
            onDiagnostic={setDiagnostic}
          />
        )}
      </div>
    );
  }

  if (!src) {
    return <div className="text-xs text-ink-500">Loading recording…</div>;
  }

  return <audio controls preload="none" src={src} className="w-full" />;
}

export function RecordingDiagnosticButton({
  callId,
  diagnosing,
  diagnostic,
  onDiagnosing,
  onDiagnostic,
}: {
  callId: string;
  diagnosing: boolean;
  diagnostic: string | null;
  onDiagnosing: (v: boolean) => void;
  onDiagnostic: (v: string | null) => void;
}) {
  const run = async (): Promise<void> => {
    onDiagnosing(true);
    onDiagnostic(null);
    try {
      const data = await api.get<{
        apiBase?: string;
        recordings: Array<{ sid: string; accountSid: string | null; status: string | null; channels: number | null; duration: string | null }>;
        media: Array<{ sid: string; auth: string; suffix: string; requestedChannels: string | null; status: number; contentType: string | null; contentLength: string | null; body: string }>;
        error?: string;
      }>(`/calls/${callId}/recording-diagnostics`);
      const recordings = data.recordings.length
        ? data.recordings.map((r) => `${r.sid} (acct:${r.accountSid?.slice(-6) ?? '?'}): ${r.status ?? 'unknown'}, channels ${r.channels ?? '?'}, ${r.duration ?? '?'}s`).join('\n')
        : `No Twilio recordings${data.error ? ` (${data.error})` : ''}`;
      const media = data.media.map((m) => {
        const line = `${m.suffix}${m.requestedChannels ? ` ch=${m.requestedChannels}` : ''} via ${m.auth}: ${m.status} ${m.contentType ?? ''} ${m.contentLength ?? ''}`.trim();
        return m.body ? `${line}\n  ${m.body.slice(0, 120)}` : line;
      }).join('\n');
      const header = data.apiBase ? `api: ${data.apiBase}` : '';
      onDiagnostic([header, recordings, media].filter(Boolean).join('\n'));
    } catch (err) {
      const code = (err as { code?: string }).code;
      onDiagnostic(code ?? 'Diagnostic failed');
    } finally {
      onDiagnosing(false);
    }
  };

  return (
    <div className="space-y-1">
      <button type="button" className="btn-secondary !px-2 !py-1 text-[11px]" disabled={diagnosing} onClick={() => void run()}>
        {diagnosing ? 'Checking Twilio...' : 'Check Twilio recording'}
      </button>
      {diagnostic && <pre className="max-h-40 overflow-auto rounded bg-ink-50 p-2 text-[10px] whitespace-pre-wrap">{diagnostic}</pre>}
    </div>
  );
}
